import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { usePropertyTransfer } from "@/hooks/usePropertyTransfer";
import { supabase } from "@/lib/supabase";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import { Button } from "@/components/ui/button";
import { Home, Loader2, CheckCircle, XCircle, AlertTriangle, Lock } from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ClaimProperty = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { session, loading: authLoading, userProfile } = useAuth();
    const user = session?.user;

    const { claimTransfer } = usePropertyTransfer();
    const { refetch } = usePropertyContext();

    const [status, setStatus] = useState("loading"); // loading, confirm, claiming, success, error
    const [transferDetails, setTransferDetails] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isOverLimit, setIsOverLimit] = useState(false);

    const token = searchParams.get("token");

    // Fetch transfer details
    useEffect(() => {
        const fetchTransferDetails = async () => {
            if (!token) {
                setStatus("error");
                setErrorMessage("No transfer token provided.");
                return;
            }

            if (authLoading) return;

            if (!user) {
                // Redirect to auth with return URL. The nested path+query must be
                // percent-encoded as a single opaque value so Auth.jsx's `redirect`
                // param round-trips intact (otherwise the token risks being mangled
                // if it ever contains reserved URL characters).
                const returnTo = `/claim-property?token=${token}`;
                navigate(`/auth?redirect=${encodeURIComponent(returnTo)}`);
                return;
            }

            try {
                // Use RPC to fetch transfer details - a direct query fails because
                // RLS only lets the SENDER read their own transfers, not the recipient.
                const { data: transfer, error } = await supabase
                    .rpc("get_transfer_details", { p_claim_token: token });

                if (error || !transfer || !transfer.id) {
                    setStatus("error");
                    setErrorMessage("Transfer not found, expired, or already claimed.");
                    return;
                }

                // Check if user is trying to claim their own transfer
                if (transfer.sender_id === user.id) {
                    setStatus("error");
                    setErrorMessage("You cannot claim your own property transfer.");
                    return;
                }

                setTransferDetails(transfer);

                // Check user's property count and tier
                const { data: properties } = await supabase
                    .from("properties")
                    .select("id")
                    .eq("user_id", user.id);

                const currentCount = properties?.length || 0;
                const maxAllowed = userProfile?.max_properties || 1;

                // Set flag if they're over limit (property will be locked but still accepted)
                setIsOverLimit(currentCount >= maxAllowed);

                // Always show confirmation dialog
                setStatus("confirm");
                setShowConfirmDialog(true);
            } catch (err) {
                console.error("Error fetching transfer details:", err);
                setStatus("error");
                setErrorMessage("Failed to load transfer details.");
            }
        };

        fetchTransferDetails();
    }, [token, user, authLoading, navigate, userProfile]);

    const handleConfirmClaim = async () => {
        setShowConfirmDialog(false);
        setStatus("claiming");

        const result = await claimTransfer(token);

        if (result) {
            // Refetch properties so the new one shows up in the switcher
            await refetch();

            // Mark user as onboarded since they now have a property
            if (user) {
                await supabase
                    .from("profiles")
                    .update({ is_onboarded: true })
                    .eq("id", user.id);
                // Force sync localStorage cache
                localStorage.setItem(`homeezi_setup_${user.id}`, "true");
            }

            setStatus("success");
            // The result might include a message about the property being locked
            if (result.is_locked) {
                setSuccessMessage(result.message || "Property transferred but is locked. Upgrade to access it.");
            } else {
                setSuccessMessage("Property transferred successfully!");
            }
        } else {
            setStatus("error");
            setErrorMessage("Failed to claim the property. Please try again.");
        }
    };

    const handleCancelClaim = () => {
        setShowConfirmDialog(false);
        navigate("/");
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] pb-24">
            <PageHeader pageName="Claim Property" />

            <main className="px-6" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 7rem)" }}>
                <div className="max-w-lg mx-auto">
                    <GlassCard className="p-8 text-center">
                        {(status === "loading" || authLoading) && (
                            <>
                                <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
                                <h2 className="text-xl font-bold text-foreground mb-2">
                                    Loading Transfer...
                                </h2>
                                <p className="text-muted-foreground">
                                    Please wait while we verify the transfer details.
                                </p>
                            </>
                        )}

                        {status === "claiming" && (
                            <>
                                <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
                                <h2 className="text-xl font-bold text-foreground mb-2">
                                    Claiming Property...
                                </h2>
                                <p className="text-muted-foreground">
                                    Please wait while we transfer the property to your account.
                                </p>
                            </>
                        )}

                        {status === "success" && (
                            <>
                                <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="w-8 h-8 text-emerald-500" />
                                </div>
                                <h2 className="text-xl font-bold text-foreground mb-2">
                                    Property Claimed!
                                </h2>
                                <p className="text-muted-foreground mb-6">
                                    {successMessage || "The property and selected items have been transferred to your account."}
                                </p>
                                <Button onClick={() => navigate("/")} className="gap-2">
                                    <Home className="w-4 h-4" />
                                    Go to Dashboard
                                </Button>
                            </>
                        )}

                        {status === "error" && (
                            <>
                                <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <XCircle className="w-8 h-8 text-destructive" />
                                </div>
                                <h2 className="text-xl font-bold text-foreground mb-2">
                                    Transfer Failed
                                </h2>
                                <p className="text-muted-foreground mb-6">
                                    {errorMessage || "The transfer link may be invalid or expired."}
                                </p>
                                <Button onClick={() => navigate("/")} variant="outline">
                                    Go to Dashboard
                                </Button>
                            </>
                        )}
                    </GlassCard>
                </div>
            </main>

            {/* Confirmation Dialog */}
            <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Property Transfer</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-3">
                            {transferDetails && (
                                <>
                                    <p>
                                        <strong>{transferDetails.sender?.name || "Someone"}</strong> is transferring{" "}
                                        <strong>"{transferDetails.property?.name}"</strong> to you.
                                    </p>

                                    <div className="bg-muted/50 p-3 rounded-lg text-left space-y-1">
                                        <p className="text-sm">
                                            <strong>Appliances:</strong> {transferDetails.selected_appliances?.length || 0}
                                        </p>
                                        <p className="text-sm">
                                            <strong>Paint Codes:</strong> {transferDetails.selected_paint_codes?.length || 0}
                                        </p>
                                        <p className="text-sm">
                                            <strong>Documents:</strong> {transferDetails.selected_house_vault?.length || 0}
                                        </p>
                                    </div>

                                    {isOverLimit && (
                                        <div className="bg-amber-500/10 border border-amber-500/20 p-3 rounded-lg text-left">
                                            <div className="flex items-start gap-2">
                                                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                                <div>
                                                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                                                        Property Limit Reached
                                                    </p>
                                                    <p className="text-xs text-amber-800 dark:text-amber-200 mt-1">
                                                        This property will be added to your account but will be locked in the property switcher until you upgrade your plan.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <p className="text-sm">
                                        Do you want to accept this property and add it to your account?
                                    </p>
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleCancelClaim}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmClaim}>
                            Accept Transfer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default ClaimProperty;
